import { Immutable } from "immer";
import React from "react";

import type { HomepageHeroPromoBox as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { WistiaBackgroundVideo } from "../WistiaBackgroundVideo";

type IProps = Immutable<{
    value: Value;
}>;

const renderMainText = (
    value: string | null | undefined,
): React.JSX.Element => {
    if (value) {
        return (
            <div className="homepage-hero__main-text">
                <RichTextBlock value={value} />
            </div>
        );
    }
    return (
        <div className="homepage-hero__main-text homepage-hero__main-text--hidden"></div>
    );
};

export const HomepageHeroPromoBox = (
    props: IProps,
): React.JSX.Element | null => {
    const v = props.value;
    const bgUrl = v.background_image?.url;

    return (
        <div
            className={concatClassNames([
                "hero",
                "homepage-hero",
                v.responsive_visibility?.visibility ?? "",
            ])}
            style={bgUrl ? { backgroundImage: `url(${bgUrl})` } : undefined}
        >
            {v.video && <WistiaBackgroundVideo videoId={v.video} />}
            <div className="l-capped-width u-flex-container homepage-hero__content-wrapper">
                <div className="homepage-hero__content">
                    <div className="desktop u-flex-container">
                        {renderMainText(v.main_text)}
                        {v.promo_box && v.promo_box.length > 0 && (
                            <div
                                className={concatClassNames([
                                    "homepage-hero__promo-container",
                                    v.theme ?? "",
                                ])}
                            >
                                <div className="homepage-hero__promo-box">
                                    <StreamField
                                        tagName={React.Fragment}
                                        value={v.promo_box}
                                        components={{
                                            text: (block) => (
                                                <RichTextBlock
                                                    value={block.value}
                                                />
                                            ),
                                            text_divider: (block) => (
                                                <div className="homepage-hero__promo-box-divider">
                                                    {block.value}
                                                </div>
                                            ),
                                        }}
                                    />
                                    {v.promo_box_footer && (
                                        <RichTextBlock
                                            value={v.promo_box_footer}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="mobile u-flex-container">
                        {renderMainText(v.main_text_mobile)}
                        {v.offers && v.offers.length > 0 && (
                            <div className="homepage-hero__offers">
                                {v.offers
                                    .filter(
                                        (offer) =>
                                            !offer.promo_period ||
                                            offer.promo_period.is_promo_active,
                                    )
                                    .map((offer, i) => (
                                        <a
                                            key={i}
                                            href={
                                                offer.relative_link ||
                                                offer.offer?.url ||
                                                ""
                                            }
                                            className="homepage-hero__offer"
                                        >
                                            <div className="homepage-hero__offer--highlight">
                                                <RichTextBlock
                                                    value={
                                                        offer.link_copy ?? ""
                                                    }
                                                />
                                            </div>
                                            <RichTextBlock
                                                value={offer.description ?? ""}
                                            />
                                        </a>
                                    ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
